import { LOCALES, type Locale } from '@nexus/core';
import {
  Avatar,
  MenuList,
  MenuListItem,
  MenuListSeparator,
  ResponsivePopover,
  SegmentedControl,
} from '@nexus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { visibleSections } from '../admin/sections';
import { useModules } from '../modules/context';
import { useSession } from '../session/session';
import { useSetting, useUpdatePersonalSetting } from '../session/settings';
import type { ThemeSetting } from '../theme/appearance';
import { useGuardedSignOut } from './sign-out';

/** Avatar menu: profile, theme and language at hand, sign out (design-system.md, Profilmenü). */
export function ProfileMenu() {
  const { t } = useTranslation();
  const session = useSession();
  const modules = useModules();
  const [open, setOpen] = useState(false);
  const theme = useSetting<ThemeSetting>('core.theme') ?? 'system';
  const locale = useSetting<Locale>('core.locale') ?? 'de';
  const update = useUpdatePersonalSetting();
  const [signOut, signOutDialog] = useGuardedSignOut();
  const name = session.account.displayName;

  return (
    <>
      <ResponsivePopover
        open={open}
        onOpenChange={setOpen}
        title={t('profile.account')}
        trigger={
          <button
            type="button"
            aria-label={t('shell.profile.open')}
            className="flex size-control cursor-pointer items-center justify-center rounded-full transition-colors transition-fast hover:bg-hover"
          >
            <Avatar name={name} />
          </button>
        }
      >
        <div className="flex items-center gap-3 border-b border-line px-4 py-3">
          <Avatar name={name} size="lg" />
          <div className="min-w-0">
            <p className="truncate font-medium text-fg">{name}</p>
            {session.account.email && (
              <p className="truncate text-xs text-fg-muted">{session.account.email}</p>
            )}
            {session.company && (
              <p className="truncate text-xs text-fg-subtle">{session.company.name}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-3 border-b border-line px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-fg-muted">{t('shell.profile.theme')}</span>
            <SegmentedControl<ThemeSetting>
              aria-label={t('shell.profile.theme')}
              iconOnly
              value={theme}
              onValueChange={(value) => update.mutate({ key: 'core.theme', value })}
              options={[
                { value: 'light', label: t('core.settings.theme.options.light'), icon: 'sun' },
                { value: 'dark', label: t('core.settings.theme.options.dark'), icon: 'moon' },
                {
                  value: 'system',
                  label: t('core.settings.theme.options.system'),
                  icon: 'monitor',
                },
              ]}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-fg-muted">{t('shell.profile.language')}</span>
            <SegmentedControl<Locale>
              aria-label={t('shell.profile.language')}
              value={locale}
              onValueChange={(value) => update.mutate({ key: 'core.locale', value })}
              options={LOCALES.map((value) => ({
                value,
                label: value.toUpperCase(),
                description: t(`core.settings.locale.options.${value}`),
              }))}
            />
          </div>
        </div>
        <MenuList>
          <MenuListItem icon="user-cog" href="/profile" onClick={() => setOpen(false)}>
            {t('shell.profile.myProfile')}
          </MenuListItem>
          <MenuListItem icon="book-open" href="/help" onClick={() => setOpen(false)}>
            {t('shell.help')}
          </MenuListItem>
          {visibleSections(session, modules).length > 0 && (
            <MenuListItem icon="settings" href="/admin" onClick={() => setOpen(false)}>
              {t('shell.admin')}
            </MenuListItem>
          )}
          <MenuListSeparator />
          <MenuListItem
            icon="log-out"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
          >
            {t('common.signOut')}
          </MenuListItem>
        </MenuList>
      </ResponsivePopover>
      {signOutDialog}
    </>
  );
}
